import type { RefreshResponse } from "@spare-cart/contracts/auth";
import type { Ok, Paginated } from "@spare-cart/contracts/common";
import { env } from "@core/env";
import { NETWORK_STATUS, toApiError } from "./errors";
import { getAccessToken, setAccessToken } from "./token";

const UNAUTHORIZED = 401;

export type QueryValue = string | number | boolean | null | undefined;

export const toQueryRecord = (
  query: Readonly<Record<string, unknown>>,
): Record<string, QueryValue> =>
  Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined)) as Record<
    string,
    QueryValue
  >;

export type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Readonly<Record<string, QueryValue>>;
  idempotencyKey?: string;
  signal?: AbortSignal;
};

export const buildUrl = (path: string, query?: Readonly<Record<string, QueryValue>>): string => {
  const url = `${env.apiBaseUrl}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const search = params.toString();
  return search.length > 0 ? `${url}?${search}` : url;
};

type RawResponse = { status: number; payload: unknown };

const dispatch = async (path: string, options: RequestOptions): Promise<RawResponse> => {
  const { method = "GET", body, query, idempotencyKey, signal } = options;
  const headers = new Headers({ Accept: "application/json" });

  const token = getAccessToken();
  if (token !== null) headers.set("Authorization", `Bearer ${token}`);
  if (body !== undefined) headers.set("Content-Type", "application/json");
  if (idempotencyKey !== undefined) headers.set("Idempotency-Key", idempotencyKey);

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      credentials: "include",
      signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    throw toApiError(NETWORK_STATUS, cause);
  }

  const payload = response.status === 204 ? null : await response.json().catch(() => null);
  return { status: response.status, payload };
};

export type RefreshOutcome =
  { kind: "ok"; data: RefreshResponse } | { kind: "rejected" } | { kind: "unavailable" };

const REFRESH_LOCK = "spare-cart:auth-refresh";
const REJECTED_STATUSES: readonly number[] = [401, 403];

// NOTE: rotation revokes the family on reuse, so tabs must refresh one at a time.
const withRefreshLock = async (task: () => Promise<RefreshOutcome>): Promise<RefreshOutcome> =>
  "locks" in navigator ? await navigator.locks.request(REFRESH_LOCK, task) : task();

const performRefresh = async (): Promise<RefreshOutcome> => {
  let result: RawResponse;
  try {
    result = await dispatch("/auth/refresh", { method: "POST", body: { audience: "customer" } });
  } catch {
    return { kind: "unavailable" };
  }
  if (REJECTED_STATUSES.includes(result.status)) return { kind: "rejected" };

  const data = (result.payload as Ok<RefreshResponse> | null)?.data;
  if (result.status < 200 || result.status >= 300 || data === undefined) {
    return { kind: "unavailable" };
  }

  setAccessToken(data.tokens.accessToken);
  return { kind: "ok", data };
};

let refreshInFlight: Promise<RefreshOutcome> | null = null;

export const refreshSession = (): Promise<RefreshOutcome> => {
  if (refreshInFlight !== null) return refreshInFlight;

  const started = withRefreshLock(performRefresh)
    .catch((): RefreshOutcome => ({ kind: "unavailable" }))
    .finally(() => {
      if (refreshInFlight === started) refreshInFlight = null;
    });

  refreshInFlight = started;
  return started;
};

const send = async (path: string, options: RequestOptions): Promise<RawResponse> => {
  const first = await dispatch(path, options);
  if (first.status !== UNAUTHORIZED) {
    if (first.status >= 400) throw toApiError(first.status, first.payload);
    return first;
  }

  if ((await refreshSession()).kind !== "ok") {
    setAccessToken(null);
    throw toApiError(first.status, first.payload);
  }

  const retry = await dispatch(path, options);
  if (retry.status === UNAUTHORIZED) setAccessToken(null);
  if (retry.status >= 400) throw toApiError(retry.status, retry.payload);
  return retry;
};

export const request = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const { payload } = await send(path, options);
  return payload === null ? (undefined as T) : (payload as Ok<T>).data;
};

export const requestList = async <T>(
  path: string,
  options: RequestOptions = {},
): Promise<Paginated<T>> => {
  const { payload } = await send(path, options);
  return payload as Paginated<T>;
};

type ReadOptions = Omit<RequestOptions, "method" | "body">;
type WriteOptions = Omit<RequestOptions, "method" | "body">;

export const apiClient = {
  get: <T>(path: string, options?: ReadOptions) => request<T>(path, { ...options, method: "GET" }),
  list: <T>(path: string, options?: ReadOptions) =>
    requestList<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: WriteOptions) =>
    request<T>(path, { ...options, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, options?: WriteOptions) =>
    request<T>(path, { ...options, method: "PATCH", body }),
  put: <T>(path: string, body?: unknown, options?: WriteOptions) =>
    request<T>(path, { ...options, method: "PUT", body }),
  remove: <T>(path: string, body?: unknown, options?: WriteOptions) =>
    request<T>(path, { ...options, method: "DELETE", body }),
} as const;
